import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "sonner";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { programsV2Api } from "@/lib/api/v2/programs";
import { cn } from "@/lib/utils";

const GRADUATION_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

interface AddUserFormData {
  email: string;
  cellPhoneNumber: string;
  programEnrolled: string;
  fullName: string;
  dateOfBirth: string;
  graduationMonth: string;
  graduationYear: string;
  deliveryMode: string;
}

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AddUserFormData) => Promise<void>;
  processing: boolean;
}

const emptyForm: AddUserFormData = {
  email: "",
  cellPhoneNumber: "",
  programEnrolled: "",
  fullName: "",
  dateOfBirth: "",
  graduationMonth: "",
  graduationYear: "",
  deliveryMode: "",
};

type ProgramPickerRow = { _id: string; title: string; status?: string };

export const AddUserDialog = React.memo(({ open, onOpenChange, onSubmit, processing }: AddUserDialogProps) => {
  const [formData, setFormData] = React.useState<AddUserFormData>(emptyForm);
  const [programs, setPrograms] = React.useState<ProgramPickerRow[]>([]);
  const [programsLoading, setProgramsLoading] = React.useState(false);
  const [programComboOpen, setProgramComboOpen] = React.useState(false);
  const [selectedProgramId, setSelectedProgramId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setFormData(emptyForm);
      setProgramComboOpen(false);
      setPrograms([]);
      setSelectedProgramId(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setProgramsLoading(true);
      try {
        // No dedicated "titles only" endpoint in this app; same list API as FAQs / home layout.
        const res = await programsV2Api.fetchAllPrograms({ page: 1, limit: 500, status: "all" });
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data?.programs)) {
          setPrograms(
            res.data.programs.map((p) => ({
              _id: p._id,
              title: (p.title || p.programId || p._id).trim(),
              status: p.status,
            })),
          );
        } else {
          setPrograms([]);
          toast.error(res?.message || "Could not load programs");
        }
      } catch {
        if (!cancelled) {
          setPrograms([]);
          toast.error("Could not load programs");
        }
      } finally {
        if (!cancelled) setProgramsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await onSubmit(formData);
    },
    [formData, onSubmit],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Create a new student. A secure password will be generated and emailed to the student.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-0">
          <div className="grid gap-5 py-1">
            <div className="grid gap-2">
              <Label htmlFor="fullName">
                Full name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
                autoComplete="name"
                data-form-type="other"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="email"
                data-form-type="other"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cellPhoneNumber">Cell phone</Label>
              <PhoneInput
                international
                defaultCountry="US"
                value={formData.cellPhoneNumber}
                onChange={(value) => setFormData((prev) => ({ ...prev, cellPhoneNumber: value || "" }))}
                className="phone-input-wrapper w-full"
                numberInputProps={{
                  id: "cellPhoneNumber",
                  autoComplete: "tel",
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="programEnrolled">Program</Label>
              {/* `modal`: required for scroll when Popover+Command is inside a Radix Dialog (body scroll-lock vs portaled popover). */}
              <Popover modal open={programComboOpen} onOpenChange={setProgramComboOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="programEnrolled"
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={programComboOpen}
                    disabled={processing || programsLoading}
                    className="h-9 w-full min-w-0 justify-between px-3 font-normal"
                  >
                    <span
                      className={cn(
                        "truncate text-left",
                        !formData.programEnrolled && "text-muted-foreground",
                      )}
                    >
                      {programsLoading ? "Loading programs…" : formData.programEnrolled || "Search and select a program…"}
                    </span>
                    <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="z-[60] flex w-[min(calc(100vw-2rem),380px)] max-h-[min(420px,70vh)] flex-col overflow-hidden p-0"
                  align="start"
                  onWheel={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                >
                  <Command className="flex h-auto min-h-0 flex-1 flex-col overflow-hidden">
                    <CommandInput placeholder="Search by title…" />
                    <CommandList className="min-h-0 max-h-[min(280px,calc(70vh-5rem))] flex-1 overflow-x-hidden overflow-y-auto overscroll-contain [scrollbar-gutter:stable]">
                      <CommandEmpty>
                        {programsLoading ? "Loading…" : programs.length === 0 ? "No programs loaded." : "No matching program."}
                      </CommandEmpty>
                      <CommandGroup className="overflow-visible p-1">
                        {programs.map((p) => (
                          <CommandItem
                            key={p._id}
                            value={`${p.title} ${p._id}`}
                            onSelect={() => {
                              setSelectedProgramId(p._id);
                              setFormData((prev) => ({ ...prev, programEnrolled: p.title }));
                              setProgramComboOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "size-4 shrink-0",
                                selectedProgramId === p._id ? "opacity-100" : "opacity-0",
                              )}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1 truncate">{p.title}</span>
                            {p.status ? (
                              <span className="shrink-0 text-xs capitalize text-muted-foreground">{p.status}</span>
                            ) : null}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      {formData.programEnrolled ? (
                        <CommandGroup className="border-t p-1">
                          <CommandItem
                            value="__clear_program__"
                            onSelect={() => {
                              setSelectedProgramId(null);
                              setFormData((prev) => ({ ...prev, programEnrolled: "" }));
                              setProgramComboOpen(false);
                            }}
                            className="text-muted-foreground"
                          >
                            Clear selection
                          </CommandItem>
                        </CommandGroup>
                      ) : null}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="dateOfBirth">Date of birth</Label>
              <Input
                id="dateOfBirth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                autoComplete="bday"
              />
            </div>
            <div className="grid gap-2">
              <p id="add-user-graduation-label" className="text-sm font-medium leading-none">
                Graduation
              </p>
              <div className="flex gap-2" role="group" aria-labelledby="add-user-graduation-label">
                <div className="min-w-0 flex-1">
                  <Select
                    value={formData.graduationMonth || undefined}
                    onValueChange={(graduationMonth) =>
                      setFormData((prev) => ({ ...prev, graduationMonth }))
                    }
                  >
                    <SelectTrigger id="graduationMonth" className="w-full">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                  <SelectContent>
                    {GRADUATION_MONTHS.map((month) => (
                      <SelectItem key={month} value={month}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                  </Select>
                </div>
                <Input
                  id="graduationYear"
                  name="graduationYear"
                  type="text"
                  inputMode="numeric"
                  value={formData.graduationYear}
                  onChange={handleInputChange}
                  placeholder="Year"
                  className="w-[5.5rem] shrink-0"
                  autoComplete="off"
                  data-form-type="other"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="deliveryMode">Delivery mode</Label>
              <Select
                value={formData.deliveryMode || undefined}
                onValueChange={(deliveryMode) =>
                  setFormData((prev) => ({ ...prev, deliveryMode }))
                }
              >
                <SelectTrigger id="deliveryMode" className="w-full">
                  <SelectValue placeholder="Select delivery mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HYBRID">Hybrid</SelectItem>
                  <SelectItem value="ONCAMPUS">On-campus</SelectItem>
                  <SelectItem value="ZOOM">Zoom</SelectItem>
                  <SelectItem value="ONLINE">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-6 gap-3 border-t border-border pt-5 sm:gap-3">
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground sm:w-auto"
              onClick={() => onOpenChange(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="w-full min-w-[10.5rem] sm:w-auto"
            >
              {processing ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Creating…
                </>
              ) : (
                "Create user"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

AddUserDialog.displayName = "AddUserDialog";
